/**
 * Phase 16 (BLOCKER 1) — Section-scoped error boundary.
 *
 * Catches React render exceptions (e.g. "Objects are not valid as a
 * React child") inside a bounded section so a downstream bug in
 * OVC/CrewMess/Buffets renders a contained inline error card instead
 * of crashing the entire day editor with the red full-page overlay.
 */
import React from "react";

export default class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface the full stack to the browser console so the user (and
    // the tester) can capture the exact location.
    // eslint-disable-next-line no-console
    console.error(
      "[SectionErrorBoundary]",
      this.props.label || "section",
      error,
      info,
    );
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      const msg = String(this.state.error?.message || this.state.error);
      return (
        <div
          className="my-2 rounded border-2 border-rose-300 bg-rose-50 p-3 text-xs text-rose-900"
          data-testid={`section-error-${this.props.testId || "section"}`}
        >
          <div className="mb-1 font-semibold">
            ⚠ {this.props.label || "This section"} failed to render.
          </div>
          <div className="mb-1 font-mono text-[10px] opacity-80">
            {msg}
          </div>
          <div className="text-[10px]">
            The rest of the day editor still works — check the browser
            console for the full stack trace.
          </div>
          <button
            type="button"
            onClick={this.reset}
            className="mt-2 rounded border border-rose-400 bg-white px-2 py-0.5 text-[10px] hover:bg-rose-100"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
